import React, { useEffect, useRef } from 'react';
import { useFontSize } from '../../contexts/FontSizeContext';
import { getTextSizeClass } from '../../utils/fontSizeUtils';

export type TabType = 'daily' | 'performance' | 'technicals' | 'fundamentals' | 'proscons' | 'analysts' | 'insiders';

export interface TabBarProps {
  /** The currently active tab */
  activeTab: TabType;
  /** Whether the tabs should be disabled (e.g., when editing) */
  disabled?: boolean;
  /** The ticker symbol for ticker-specific pages (optional) */
  ticker?: string;
  /** The public UUID for public list pages (optional) */
  publicUuid?: string | null;
}

const TabBar: React.FC<TabBarProps> = ({ 
  activeTab, 
  disabled = false, 
  ticker,
  publicUuid
}) => {
  const { fontSize } = useFontSize();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const baseClasses = `px-2 sm:px-4 py-3 font-semibold ${getTextSizeClass(fontSize)} transition-all duration-200 whitespace-nowrap flex-shrink-0`;
  const disabledClasses = disabled ? "pointer-events-none opacity-50" : "";
  
  const getTabClasses = (tab: TabType) => {
    const isActive = activeTab === tab;
    
    // Only apply disabled classes if disabled is true AND it's not a public list (public lists should allow tab navigation)
    const shouldDisable = disabled && !publicUuid;
    const disabledTabClasses = shouldDisable ? "pointer-events-none opacity-50" : "";
    
    if (isActive) {
      return `${baseClasses} border-b-[3px] border-highlight-gold text-gray-900 dark:text-highlight-gold -mb-px ${disabledTabClasses}`;
    }
    
    return `${baseClasses} text-gray-500 dark:text-text-secondary hover:text-gray-700 dark:hover:text-highlight-gold hover:border-b-2 hover:border-highlight-gold ${disabledTabClasses}`;
  };

  const getTabHref = (tab: TabType) => {
    if (ticker) {
      const basePath = `/ticker/${ticker}/${tab}`;
      if (publicUuid) {
        return `${basePath}?public_uuid=${publicUuid}`;
      }
      return basePath;
    }
    const basePath = `/${tab}`;
    if (publicUuid) {
      return `${basePath}?public_uuid=${publicUuid}`;
    }
    return basePath;
  };

  const tabs: { type: TabType; label: string }[] = [
    { type: 'daily', label: 'Daily' },
    { type: 'performance', label: 'Performance' },
    { type: 'technicals', label: 'Technicals' },
    { type: 'fundamentals', label: 'Fundamentals' },
    { type: 'proscons', label: 'Pros & Cons' },
    { type: 'analysts', label: 'Analysts' },
    { type: 'insiders', label: 'Insiders' },
  ];

  // Auto-scroll to the end when the last tab (Insiders) is active
  useEffect(() => {
    if (activeTab === 'insiders' && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      // Use setTimeout to ensure the DOM has updated
      setTimeout(() => {
        container.scrollTo({
          left: container.scrollWidth - container.clientWidth,
          behavior: 'smooth'
        });
      }, 100);
    }
  }, [activeTab]);

  return (
    <div ref={scrollContainerRef} className="flex overflow-x-auto scrollbar-hide space-x-1 sm:space-x-2 md:space-x-4 lg:space-x-8">
      {tabs.map(({ type, label }) => (
        <a
          key={type}
          className={getTabClasses(type)}
          href={getTabHref(type)}
        >
          {label}
        </a>
      ))}
    </div>
  );
};

export default TabBar;
